import { EventEmitter } from '@angular/core';
import { ClientErrorCodes } from '@models';
import {
    isHtml,
    isJson,
} from '@utils/string.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

// tslint:disable-next-line:max-line-length
const dateRegex: RegExp = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/;
// tslint:disable-next-line:max-line-length
const isoDateRegex: RegExp = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d)|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d)/;

const isStringUTCDate = (str: any) => {
    return typeof str === 'string' &&
        !isJson(str) &&
        (
            dateRegex.test(str) ||
            isoDateRegex.test(str)
        ) &&
        moment(str).isValid();
};

export const compareObjects = (a: any, b: any): boolean => {
    if (a === b) { return true; }

    if (a === null || b === null) { return false; }

    if (a instanceof Array && b instanceof Array) {
        if (a.length !== b.length) { return false; }

        let areEqual = true;
        for (let i = 0; i < a.length; i++) {
            areEqual = areEqual && compareObjects(a[i], b[i]);
        }
        return areEqual;
    }

    if (a instanceof Object && b instanceof Object) {
        const keys = new Set<string>();
        Object.keys(a).forEach(key => keys.add(key));
        Object.keys(b).forEach(key => keys.add(key));
        if (keys.size === 0) {
            return true;
        }
        let areEqual = true;
        keys.forEach(key => {
            if (a.hasOwnProperty(key) && b.hasOwnProperty(key)) {
                areEqual = areEqual && compareObjects(a[key], b[key]);
            } else {
                areEqual = false;
            }
        });
        return areEqual;
    }

    return a === b;
};

export function getProperty(object: any, propertyChain: string, translatePrefix = '') {
    const chain = propertyChain.split('.');
    let value: any = object;
    chain.forEach(prop => {
        if (value == null) { return value; }
        value = value[prop];
    });
    return value != null && typeof value === 'string' ? translatePrefix + value : value;
}

export function extractErrorMessage(
    error: any,
    defaultMessage = `An unexpected error has occured (#${ClientErrorCodes.GenericError})`,
): string {
    const errorBody: any =
        getProperty(error, 'error.error') ||
        getProperty(error, 'error') ||
        error;
    const errorCode = errorBody.errorCode || (errorBody.error ? errorBody.error.code : errorBody.code);
    let message: string;
    if (typeof errorBody === 'object') {
        if (errorBody.innererror) {
            // ABP exceptions
            message = errorBody.innererror.message.replace('[', '').replace(']', '');
        } else {
            // other services exceptions
            message = errorBody.message;
        }
    } else if (isHtml(errorBody) || typeof errorBody !== 'string') {
        message = defaultMessage;
    } else {
        message = errorBody;
    }

    return errorCode == null ? message : `${message} (#${errorCode})`;
}

export function toDate<T extends any>(obj: T): T {
    if (obj == null) { return obj; }

    if (obj instanceof Array) {
        return obj.map((entry: any) => toDate(entry));
    }

    for (const key of Object.keys(obj)) {
        if (isArray(obj[key])) {
            obj[key] = obj[key]
                .map(
                    (subEntry: any) => isStringUTCDate(subEntry) ?
                        moment(subEntry).toDate() :
                        toDate(subEntry),
                );
            continue;
        }

        if (isObject(obj[key])) {
            obj[key] = toDate(obj[key]);
            continue;
        }

        const checkedString = obj[key];
        if (
            !key.endsWith('Str') &&
            isStringUTCDate(checkedString)
        ) {
            obj[key] = moment(checkedString)
                .toDate();
        }
    }

    return obj;
}

export function serializeMomentInstance(date: moment.Moment) {
    const isoString = date.toISOString();
    return `${isoString.substr(0, isoString.length - 5)}Z`;
}

export function serializeDates<T extends any>(obj: T) {
    // serialize Date and Moment instances into ISO String without sub-second precision
    if (obj == null) { return obj; }

    if (moment.isMoment(obj)) {
        return serializeMomentInstance(obj as moment.Moment);
    }

    if (obj instanceof Date) {
        return serializeMomentInstance(moment(obj));
    }

    if (obj instanceof Array) {
        return obj.map((entry: any) => serializeDates(entry));
    }

    const objClone = cloneDeep(obj);
    for (const key of Object.keys(objClone)) {
        if (isArray(objClone[key])) {
            objClone[key] = objClone[key]
                .map((subEntry: any) => serializeDates(subEntry));
            continue;
        }

        if (isObject(objClone[key])) {
            objClone[key] = serializeDates(objClone[key]);
            continue;
        }
    }

    return objClone;
}

export function unsubscribe(...subscriptions: Array<Subscription | EventEmitter<any> | undefined>) {
    subscriptions.forEach(subscription => {
        if (
            (
                subscription instanceof Subscription ||
                subscription instanceof EventEmitter
            ) &&
            !subscription.closed
        ) {
            subscription.unsubscribe();
        }
    });
}

export const safeParse = (obj: string, defaultValue: any = null) => {
    if (obj == null) { return defaultValue; }

    try {
        return JSON.parse(obj);
    } catch {
        return defaultValue;
    }
};

export const safeTrim = (obj: any) => typeof obj === 'string' ? obj.trim() : obj;

export const addToArrayIf = <T>(condition: boolean, ...elements: T[]) => {
    return condition ? elements : [];
};

export const addToObjectIf = <T>(condition: boolean, element: T) => {
    return condition ? element : {};
};

export const pick = <T, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> => {
    const ret: any = {};
    keys.forEach(key => {
        ret[key] = obj[key];
    });
    return ret;
};
